import { useState, useEffect } from "react";
import {
  Building2, Users, BookOpen, TrendingUp, DollarSign, ChevronRight,
  ArrowUpRight, ArrowDownRight, CheckCircle, Clock, Zap, Shield, ClipboardCheck,
  Send, Megaphone, BarChart3, AlertTriangle, Award, Trophy, Play,
  HelpCircle, GraduationCap, Activity, FileText, Package, Globe,
  CreditCard, Receipt, UserCheck, Loader2
} from "lucide-react";
import { AdminLayout } from "../../components/layout/AdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Progress } from "../../components/ui/progress";
import { useAuth } from "../../context/AuthContext";
import { useNavigate } from "react-router-dom";
import api, { tenantAPI } from "../../lib/api";
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip as RTooltip, ResponsiveContainer, AreaChart, Area
} from "recharts";

const SuperAdminDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [kpi, setKpi] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [revenue, setRevenue] = useState({ total: 0, mrr: 0, pending: 0, invoices_count: 0 });
  const [requestCounts, setRequestCounts] = useState({ pending: 0, total: 0 });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [kpiRes, tenantsRes] = await Promise.all([
          api.get("/analytics/kpi-dashboard"),
          tenantAPI.list(),
        ]);
        setKpi(kpiRes.data);
        setTenants(tenantsRes.data || []);

        // Fetch revenue from invoices
        try {
          const invRes = await api.get("/invoices");
          const invoices = invRes.data?.invoices || invRes.data || [];
          if (Array.isArray(invoices)) {
            const total = invoices.reduce((s, i) => s + (i.amount || 0), 0);
            const pending = invoices.filter(i => i.status === "pending").reduce((s, i) => s + (i.amount || 0), 0);
            setRevenue({ total, mrr: Math.round(total / 12), pending, invoices_count: invoices.length });
          }
        } catch {}

        // Fetch request counts
        try {
          const reqRes = await api.get("/requests?status=pending");
          setRequestCounts({ pending: reqRes.data?.total || 0, total: (reqRes.data?.status_counts?.pending || 0) + (reqRes.data?.status_counts?.approved || 0) + (reqRes.data?.status_counts?.completed || 0) + (reqRes.data?.status_counts?.rejected || 0) });
        } catch {}

      } catch (error) {
        console.error("Dashboard fetch error:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <AdminLayout title="Dashboard" variant="super_admin">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
        </div>
      </AdminLayout>
    );
  }

  const u = kpi?.users || {};
  const c = kpi?.courses || {};
  const e = kpi?.enrollments || {};
  const s = kpi?.sessions || {};
  const comp = kpi?.compliance || {};
  const assess = kpi?.assessments || {};
  const cert = kpi?.certificates || {};
  const sup = kpi?.support || {};
  const gam = kpi?.gamification || {};
  const activeTenants = tenants.filter(t => t.status === "active").length || tenants.length;

  const heroStats = [
    { label: "Total Revenue", value: `$${revenue.total.toLocaleString()}`, sub: `$${revenue.mrr.toLocaleString()} MRR`, icon: DollarSign, color: "text-emerald-400", bgColor: "bg-emerald-500/15 border-emerald-500/20", link: "/admin/invoices" },
    { label: "Active Tenants", value: activeTenants, sub: `${tenants.length} total`, icon: Building2, color: "text-blue-400", bgColor: "bg-blue-500/15 border-blue-500/20", link: "/admin/tenants" },
    { label: "Total Users", value: u.total || 0, sub: `+${u.new_7d || 0} this week`, icon: Users, color: "text-violet-400", bgColor: "bg-violet-500/15 border-violet-500/20", link: "/admin/users" },
    { label: "Active Courses", value: c.published || 0, sub: `${c.total || 0} total`, icon: BookOpen, color: "text-amber-400", bgColor: "bg-amber-500/15 border-amber-500/20", link: "/admin/courses" },
  ];

  const kpiCards = [
    { title: "Total Enrollments", value: e.total || 0, sub: `+${e.new_30d || 0} this month`, icon: GraduationCap, link: "/admin/analytics", trend: e.new_30d > 0 ? "up" : "flat" },
    { title: "Completion Rate", value: `${e.completion_rate || 0}%`, sub: `${e.completed || 0} completed`, icon: CheckCircle, link: "/admin/analytics", trend: (e.completion_rate || 0) > 50 ? "up" : "down" },
    { title: "Avg Score", value: `${assess.avg_score || 0}%`, sub: `${assess.total || 0} assessments`, icon: ClipboardCheck, link: "/admin/reports", trend: (assess.avg_score || 0) > 70 ? "up" : "down" },
    { title: "Certificates Issued", value: cert.total || 0, sub: `${cert.issued_30d || 0} this month`, icon: Award, link: "/admin/reports", trend: "up" },
    { title: "ILT Sessions", value: s.total || 0, sub: `${s.upcoming || 0} upcoming`, icon: Play, link: "/admin/sessions", trend: "flat" },
    { title: "Support Tickets", value: sup.total || 0, sub: `${sup.open || 0} open`, icon: HelpCircle, link: "/admin/support", trend: (sup.open || 0) > 5 ? "down" : "up" },
    { title: "SCORM Packages", value: c.scorm_count || c.published || 0, sub: `${e.avg_progress || 0}% avg progress`, icon: Package, link: "/admin/courses", trend: "flat" },
    { title: "Compliance", value: comp.templates || 0, sub: `${comp.overdue || 0} overdue`, icon: Shield, link: "/admin/compliance-dashboard", trend: (comp.overdue || 0) > 0 ? "down" : "up" },
    { title: "Pending Revenue", value: `$${revenue.pending.toLocaleString()}`, sub: `${revenue.invoices_count} invoices`, icon: Receipt, link: "/admin/invoices", trend: "flat" },
    { title: "Badges Earned", value: gam.badges_earned || 0, sub: `${gam.total_points || 0} pts total`, icon: Trophy, link: "/admin/analytics", trend: "up" },
    { title: "Pending Requests", value: requestCounts.pending, sub: "custom module & embed", icon: Send, link: "/admin/service-requests", trend: requestCounts.pending > 0 ? "down" : "up" },
    { title: "Active Users", value: u.active || 0, sub: `${Math.round(((u.active || 0) / (u.total || 1)) * 100)}% of total`, icon: UserCheck, link: "/admin/users", trend: "up" },
  ];

  return (
    <AdminLayout title="Dashboard" variant="super_admin">
      <div className="space-y-6 max-w-[1600px] mx-auto">
        {/* Welcome */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <div>
            <Badge variant="secondary" className="gap-1.5 mb-1">
              <Building2 className="w-3.5 h-3.5" /> Odesie Admin Portal
            </Badge>
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-foreground" style={{ fontFamily: "'Sora', sans-serif" }}>
              Welcome back, <span className="bg-gradient-to-r from-blue-500 to-blue-700 bg-clip-text text-transparent">{user?.full_name?.split(" ")[0] || "Admin"}</span>
            </h1>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => navigate("/admin/analytics")} className="gap-1" data-testid="go-analytics">
              <BarChart3 className="w-4 h-4" /> Analytics
            </Button>
            <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white gap-1" onClick={() => navigate("/admin/compliance-dashboard")} data-testid="go-compliance">
              <Activity className="w-4 h-4" /> Compliance
            </Button>
          </div>
        </div>

        {/* Hero Revenue Card */}
        <Card className="overflow-hidden border-0 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white relative" data-testid="revenue-hero">
          <div className="absolute inset-0 overflow-hidden">
            <div className="absolute -top-1/2 -right-1/4 w-[400px] h-[400px] rounded-full bg-gradient-to-br from-blue-500/20 to-cyan-500/10 blur-3xl" />
          </div>
          <CardContent className="relative z-10 p-5 sm:p-6 lg:p-8">
            <div className="flex items-center gap-2 mb-4">
              <DollarSign className="w-5 h-5 text-emerald-400" />
              <span className="text-xs font-medium text-white/50 uppercase tracking-wider">Platform Overview</span>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              {heroStats.map((stat, i) => (
                <button
                  key={i}
                  onClick={() => navigate(stat.link)}
                  className={`flex items-center gap-3 px-4 py-3 rounded-xl ${stat.bgColor} border transition-all hover:scale-[1.02] text-left`}
                  data-testid={`hero-stat-${i}`}
                >
                  <stat.icon className={`w-5 h-5 ${stat.color} shrink-0`} />
                  <div className="min-w-0">
                    <div className={`text-lg sm:text-xl font-bold ${stat.color}`}>{stat.value}</div>
                    <div className="text-[11px] text-white/50 truncate">{stat.label}</div>
                    <div className="text-[10px] text-white/35">{stat.sub}</div>
                  </div>
                </button>
              ))}
            </div>
            <div className="flex gap-2 mt-4">
              <Button size="sm" className="bg-white/10 hover:bg-white/20 text-white border border-white/20" onClick={() => navigate("/admin/analytics")}>
                Full Analytics <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
              <Button size="sm" className="bg-white/10 hover:bg-white/20 text-white border border-white/20" onClick={() => navigate("/admin/service-requests")}>
                Service Requests {requestCounts.pending > 0 && <Badge className="ml-1.5 bg-red-500 text-white text-[10px] px-1.5">{requestCounts.pending}</Badge>}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* KPI Grid — ALL linked */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-3" data-testid="kpi-stats-grid">
          {kpiCards.map((stat, idx) => (
            <button
              key={idx}
              onClick={() => navigate(stat.link)}
              className="text-left group"
              data-testid={`kpi-stat-${idx}`}
            >
              <Card className="hover:shadow-md hover:border-blue-500/30 transition-all h-full">
                <CardContent className="p-3.5">
                  <div className="flex items-center justify-between mb-2">
                    <div className="w-8 h-8 rounded-lg bg-blue-50 dark:bg-blue-950/30 flex items-center justify-center">
                      <stat.icon className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                    </div>
                    {stat.trend === "up" && <ArrowUpRight className="w-3.5 h-3.5 text-emerald-500" />}
                    {stat.trend === "down" && <ArrowDownRight className="w-3.5 h-3.5 text-red-500" />}
                  </div>
                  <p className="text-lg font-extrabold tracking-tight">{stat.value}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{stat.title}</p>
                  {stat.sub && <p className="text-[10px] text-muted-foreground/70">{stat.sub}</p>}
                </CardContent>
              </Card>
            </button>
          ))}
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-2">
          {[
            { label: "Tenants", icon: Building2, path: "/admin/tenants" },
            { label: "Users", icon: Users, path: "/admin/users" },
            { label: "Courses", icon: BookOpen, path: "/admin/courses" },
            { label: "Announcements", icon: Megaphone, path: "/admin/announcements" },
            { label: "Compliance", icon: Shield, path: "/admin/compliance-dashboard" },
            { label: "Reports", icon: FileText, path: "/admin/reports" },
          ].map((action, i) => (
            <button key={i} onClick={() => navigate(action.path)}
              className="flex items-center gap-2.5 p-3 rounded-xl bg-muted/40 hover:bg-blue-50 dark:hover:bg-blue-950/20 border border-transparent hover:border-blue-200 dark:hover:border-blue-800/50 transition-all group"
              data-testid={`sa-quick-action-${i}`}>
              <action.icon className="w-4 h-4 text-blue-600 dark:text-blue-400 shrink-0" />
              <span className="text-sm font-medium">{action.label}</span>
              <ChevronRight className="w-3 h-3 text-muted-foreground ml-auto opacity-0 group-hover:opacity-100 transition-opacity" />
            </button>
          ))}
        </div>

        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Enrollment Trend */}
          <Card data-testid="enrollment-trend">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm flex items-center gap-2">
                  <TrendingUp className="w-4 h-4 text-muted-foreground" /> 30-Day Enrollment Trend
                </CardTitle>
                <Button variant="ghost" size="sm" onClick={() => navigate("/admin/analytics")} className="text-xs h-7">
                  View All <ChevronRight className="w-3 h-3 ml-1" />
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="h-[200px]">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={kpi?.enrollment_trend || []}>
                    <defs>
                      <linearGradient id="enrollGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#3B82F6" stopOpacity={0.3} />
                        <stop offset="100%" stopColor="#3B82F6" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis dataKey="date" tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }}
                      tickFormatter={v => new Date(v).toLocaleDateString("en-US", { month: "short", day: "numeric" })} interval={6} />
                    <YAxis tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} />
                    <RTooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }} />
                    <Area type="monotone" dataKey="enrollments" stroke="#3B82F6" fill="url(#enrollGrad)" strokeWidth={2} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Top Courses */}
          <Card data-testid="top-courses">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Trophy className="w-4 h-4 text-amber-500" /> Top Courses
                </CardTitle>
                <Button variant="ghost" size="sm" onClick={() => navigate("/admin/courses")} className="text-xs h-7">
                  View All <ChevronRight className="w-3 h-3 ml-1" />
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-2.5">
                {(kpi?.top_courses || []).slice(0, 6).map((course, idx) => (
                  <button key={course.course_id} onClick={() => navigate(`/admin/courses/${course.course_id}`)}
                    className="flex items-center gap-3 w-full text-left hover:bg-muted/50 p-1.5 rounded-lg transition-colors" data-testid={`top-course-${idx}`}>
                    <span className="text-xs font-bold text-muted-foreground w-5 text-right">{idx + 1}</span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{course.title}</p>
                    </div>
                    <Progress value={Math.min((course.completions / (kpi.top_courses[0]?.completions || 1)) * 100, 100)} className="w-20 h-1.5" />
                    <span className="text-xs font-bold w-7 text-right">{course.completions}</span>
                  </button>
                ))}
                {(!kpi?.top_courses || kpi.top_courses.length === 0) && (
                  <p className="text-sm text-muted-foreground text-center py-8">No course data yet</p>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </AdminLayout>
  );
};

export default SuperAdminDashboard;
